import React from 'react';
import { Cpu } from 'lucide-react';
import MetricGroup, { MetricField } from '../MetricGroup';
import type { StreamStats, ServerStats } from '../../../types';
import { getLevel, ThresholdLevel, resolutionToKey } from '../../../utils/thresholds';

interface Props {
  stats: StreamStats;
  serverStats: ServerStats | null;
}

const EncodeOutput: React.FC<Props> = ({ stats, serverStats }) => {
  const sv = serverStats;
  const dominant = sv?.dominant_engine || 'default';
  const ed = sv?.per_engine && dominant !== 'default'
    ? (sv.per_engine as any)[dominant]
    : null;

  // QP（30s 直方图）
  const qp30s = ed?.qp_30s;
  const qpKf = qp30s?.keyframe_avg ?? -1;
  const qpDelta = qp30s?.delta_avg ?? -1;
  const qpAvg = qpDelta >= 0 ? qpDelta : (qp30s?.max ?? -1);
  // Stage 3F.1: NVENC AV1 已启用 QP（范围 0-255），按 codec 走 thresholds 二维查表
  // libaom AV1 仍未采集，QP=-1 自然 normal
  const qpLevel: ThresholdLevel = qpAvg < 0
    ? 'normal'
    : getLevel('qp_avg', qpAvg, dominant);

  // frame_size（30s）
  const fs30s = ed?.frame_size_30s;
  const frameSizeKf = fs30s?.keyframe_avg_bytes ?? 0;
  const frameSizeDelta = fs30s?.delta_avg_bytes ?? 0;
  const frameSizeP95 = fs30s?.delta_p95_bytes ?? 0;

  // FrameType（I/P 比例）
  const ft30s = ed?.frame_type_30s;
  const ipRatioX100 = ft30s?.iframe_pframe_size_ratio_x100 ?? 0;
  const kfShare = ft30s?.bitrate_keyframe_share_pct ?? 0;
  const kfShareLevel: ThresholdLevel = getLevel('bitrate_keyframe_share_pct', kfShare);

  // 编码耗时
  const encodeMs = stats.latencyBreakdown.encode || 0;
  const resKey = sv ? resolutionToKey(sv.width, sv.height) : 'default';
  const encodeMsLevel = getLevel('encode_ms_avg', encodeMs, dominant, resKey);

  const fields: MetricField[] = [];

  // QP 部分
  // Stage 3F.1: NVENC AV1 已启用 QP 采集（范围 0-255），libaom AV1 仍未采（永远 -1 → N/A）
  const isLibaomAv1 = /libaom/i.test(stats.encoderName || '') || dominant === 'libaom_av1';
  if (isLibaomAv1) {
    fields.push({
      label: 'QP',
      value: 'N/A',
      tooltip: 'libaom AV1 暂不采集 QP（webrtc 内置 encoder 未暴露 QP API）',
    });
  } else if (qpKf >= 0 || qpDelta >= 0) {
    fields.push({
      label: 'QP keyframe',
      value: qpKf >= 0 ? qpKf : '—',
      tooltip: 'keyframe 平均 QP（30s）',
    });
    fields.push({
      label: 'QP delta',
      value: qpDelta >= 0 ? qpDelta : '—',
      level: qpLevel,
      tooltip: 'P 帧平均 QP（30s）',
    });
    if (qp30s) {
      fields.push({
        label: 'QP 范围',
        value: `${qp30s.min}-${qp30s.max}`,
        tooltip: `30s 内最低/最高 QP（${qp30s.total_samples} 样本）`,
      });
    }
  }

  // 编码耗时
  fields.push({
    label: '编码耗时',
    value: encodeMs.toFixed(2),
    unit: 'ms',
    level: encodeMsLevel,
  });

  // 帧大小
  if (frameSizeKf > 0 || frameSizeDelta > 0) {
    fields.push({ label: 'I 帧均值', value: frameSizeKf, unit: 'B' });
    fields.push({ label: 'P 帧均值', value: frameSizeDelta, unit: 'B' });
    fields.push({ label: 'P 帧 p95', value: frameSizeP95, unit: 'B' });
  }

  // I/P 比 + I 帧码率占比
  if (ipRatioX100 > 0) {
    fields.push({
      label: 'I/P 大小比',
      value: (ipRatioX100 / 100).toFixed(1) + '×',
      tooltip: 'I 帧均值 / P 帧均值',
    });
  }
  if (kfShare > 0) {
    fields.push({
      label: 'I 帧码率占比',
      value: kfShare,
      unit: '%',
      level: kfShareLevel,
      tooltip: '>25% 黄、>40% 红 — keyframe 占用过多带宽',
    });
  }

  // 卡片级别取最严重
  const levels: ThresholdLevel[] = [qpLevel, encodeMsLevel, kfShareLevel];
  const cardLevel: ThresholdLevel = levels.includes('crit')
    ? 'crit'
    : levels.includes('warn')
    ? 'warn'
    : 'normal';

  return (
    <MetricGroup
      title="编码输出"
      icon={<Cpu size={14} />}
      level={cardLevel}
      primary={{
        label: '当前 QP / 编码耗时',
        value: qpAvg >= 0
          ? `QP ${qpAvg}`
          : `${encodeMs.toFixed(2)} ms`,
        level: cardLevel,
      }}
      fields={fields}
    />
  );
};

export default EncodeOutput;
